import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  Brain,
  ColorPicker,
  NavArrowRight,
  Usb,
} from 'iconoir-react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useChatMentionSearch } from 'features/chat/agent-chat-module/hooks/use-chat-mention-search';
import type { MentionPickerItem } from 'features/chat/utils/normalize-mention-search';
import { ConversationMenuIcon } from 'components/blocks/layout/conversation-menu-icon';
import { DocStarIcon } from 'components/blocks/layout/doc-star-icon';
import { useTheme } from 'components/core/theme-provider';
import { useToast } from 'hooks/use-toast';
import { useConfig } from 'hooks/use-config';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useUpdateProject } from 'features/projects/hooks/use-create-project';
import { useProjects } from 'features/projects/hooks/use-projects';
import { computeSubmenuFlyoutPosition } from 'features/chat/utils/submenu-flyout-position';
import { isAgentChatConversation } from 'features/chat/utils/is-agent-chat-conversation';
import { cn } from 'lib/utils';

const menuItemHoverClass = 'rounded-md hover:bg-[#F6F6F6] dark:hover:bg-muted';
const menuItemDisabledClass = 'opacity-50 cursor-not-allowed pointer-events-none';

const projectSubmenuPanelClass =
  'min-w-[220px] max-w-[280px] max-h-[280px] overflow-y-auto rounded-lg border border-[#e7e7e7] bg-[#fff] p-1 shadow-[0_10px_20px_0_rgba(173,174,163,0.27)] dark:border-border dark:bg-popover dark:shadow-md';

function runFlyoutItemAction(action: () => void, onCloseMenu?: () => void) {
  action();
  globalThis.setTimeout(() => onCloseMenu?.(), 0);
}

export type ChatPlusMenuSection = 'main' | 'footer' | 'all';

type OpenSubmenu = 'project' | 'skills' | null;

export interface ChatPlusMenuExtrasProps {
  section?: ChatPlusMenuSection;
  /** Use tap/click to toggle flyout submenus (for touch / responsive menu). */
  useTapSubmenus?: boolean;
  onClose?: () => void;
  onSelectSkill?: (skill: MentionPickerItem) => void;
  onOpenNewProject?: () => void;
  onToggleConnectors?: () => void;
  isConnectorsActive?: boolean;
  showLeadingSeparator?: boolean;
}

function MenuSeparator() {
  return <div className="my-2 h-px bg-border" />;
}

function MenuIcon({ children, textClass }: Readonly<{ children: ReactNode; textClass: string }>) {
  return (
    <div className={cn('flex h-4 w-4 shrink-0 items-center justify-center', textClass)}>
      {children}
    </div>
  );
}

export function ChatPlusMenuExtras({
  section = 'all',
  useTapSubmenus = false,
  onClose,
  onSelectSkill,
  onOpenNewProject,
  onToggleConnectors,
  isConnectorsActive = false,
  showLeadingSeparator = true,
}: ChatPlusMenuExtrasProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { toast } = useToast();
  const menuItemTextClass = theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]';
  const menuItemLabelClass = cn(
    'font-sans text-sm font-normal leading-[1.2]',
    menuItemTextClass
  );
  const { configurationSettings } = useConfig();
  const [searchParams] = useSearchParams();
  const usePortalFlyout = useTapSubmenus;
  const sidebarProjectsList = useChatStore((state) => state.sidebarProjectsList);
  const sidebarConversationsList = useChatStore((state) => state.sidebarConversationsList);
  const conversations = useChatStore((state) => state.conversations);
  const fetchAndUpdateProjectAllConversations = useChatStore(
    (state) => state.fetchAndUpdateProjectAllConversations
  );
  const removeConversationFromSidebarById = useChatStore(
    (state) => state.removeConversationFromSidebarById
  );
  const activeConversationId = useChatStore((state) => state.activeConversationId);
  const { mutateAsync: updateProject } = useUpdateProject();
  const { data: fetchedProjects } = useProjects({
    page: 1,
    pageSize: 50,
    order: 'desc',
    searchQuery: '',
  });

  const activeConversation = activeConversationId ? conversations[activeConversationId] : undefined;
  const agentIdFromUrl = searchParams.get('agentId') || searchParams.get('agent');
  const isAgentChat = isAgentChatConversation({
    conversationId: activeConversationId,
    agentIdFromUrl,
    storeConversation: activeConversation,
    sidebarConversationsList,
  });
  const canAddToProject =
    !!activeConversationId && activeConversation?.isGroupChat !== true && !isAgentChat;

  const effectiveProjects = sidebarProjectsList.length ? sidebarProjectsList : fetchedProjects ?? [];

  const showMain = section === 'main' || section === 'all';
  const showFooter = section === 'footer' || section === 'all';

  const [openSubmenu, setOpenSubmenu] = useState<OpenSubmenu>(null);
  const [flyoutPosition, setFlyoutPosition] = useState<{ top: number; left: number } | null>(null);
  const submenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const projectTriggerRef = useRef<HTMLButtonElement>(null);
  const skillsTriggerRef = useRef<HTMLButtonElement>(null);

  const { data: skillsData, isFetching: isSkillsLoading } = useChatMentionSearch(
    '',
    openSubmenu === 'skills',
    ['skill']
  );
  const skills = skillsData?.items ?? [];

  const clearSubmenuTimeout = () => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
      submenuTimeoutRef.current = null;
    }
  };

  const openSubmenuPanel = (submenu: Exclude<OpenSubmenu, null>) => {
    clearSubmenuTimeout();
    setOpenSubmenu(submenu);
  };

  const scheduleCloseSubmenu = () => {
    clearSubmenuTimeout();
    submenuTimeoutRef.current = setTimeout(() => {
      setOpenSubmenu(null);
    }, 300);
  };

  const toggleSubmenu = (submenu: Exclude<OpenSubmenu, null>) => {
    setOpenSubmenu((prev) => (prev === submenu ? null : submenu));
  };

  const updateFlyoutPosition = useCallback(() => {
    if (!usePortalFlyout || !openSubmenu) {
      setFlyoutPosition(null);
      return;
    }

    const anchor =
      openSubmenu === 'project' ? projectTriggerRef.current : skillsTriggerRef.current;
    if (!anchor) return;

    setFlyoutPosition(computeSubmenuFlyoutPosition(anchor.getBoundingClientRect()));
  }, [openSubmenu, usePortalFlyout]);

  useLayoutEffect(() => {
    updateFlyoutPosition();
  }, [updateFlyoutPosition]);

  useEffect(() => {
    if (!usePortalFlyout || !openSubmenu) return;

    window.addEventListener('resize', updateFlyoutPosition);
    window.addEventListener('scroll', updateFlyoutPosition, true);
    return () => {
      window.removeEventListener('resize', updateFlyoutPosition);
      window.removeEventListener('scroll', updateFlyoutPosition, true);
    };
  }, [openSubmenu, updateFlyoutPosition, usePortalFlyout]);

  const handleAddToProject = async (projectId: string) => {
    if (!activeConversationId || !projectId) return;

    try {
      const toProject = effectiveProjects.find((p) => p.id === projectId);
      if (!toProject) return;

      await updateProject({
        payload: { projectId, conversationId: activeConversationId },
        skipInvalidate: true,
      });
      await fetchAndUpdateProjectAllConversations(configurationSettings, projectId);
      removeConversationFromSidebarById(activeConversationId);
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('MOVE_SUCCESS_DESCRIPTION'),
      });
      setOpenSubmenu(null);
      onClose?.();
    } catch {
      toast({
        variant: 'destructive',
        title: t('MOVE_FAILED'),
        description: t('MOVE_FAILED_DESCRIPTION'),
      });
    }
  };

  const renderMenuButton = ({
    label,
    icon,
    onClick,
    disabled = false,
    showChevron = false,
    isActive = false,
  }: {
    label: string;
    icon: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    showChevron?: boolean;
    isActive?: boolean;
  }) => (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 px-1 py-1.5 text-left transition-colors',
        menuItemHoverClass,
        disabled && menuItemDisabledClass,
        isActive && 'bg-[#F6F6F6] dark:bg-muted'
      )}
    >
      <MenuIcon textClass={menuItemTextClass}>{icon}</MenuIcon>
      <div className="flex flex-1 items-center justify-between gap-2">
        <span className={menuItemLabelClass}>{label}</span>
        {showChevron && (
          <NavArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        )}
      </div>
    </button>
  );

  const renderFlyoutSubmenuItem = ({
    label,
    icon,
    onClick,
  }: {
    label: string;
    icon: ReactNode;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }}
      className={cn(
        'flex w-full items-center gap-3 px-1 py-1.5 text-left transition-colors',
        menuItemHoverClass
      )}
    >
      <MenuIcon textClass={menuItemTextClass}>{icon}</MenuIcon>
      <span className={cn('min-w-0 flex-1 truncate', menuItemLabelClass)}>{label}</span>
    </button>
  );

  const renderSkillSubmenuIcon = () => <DocStarIcon className="h-4 w-4" />;

  const renderFlyoutPanel = (content: ReactNode) => {
    if (usePortalFlyout) {
      if (!flyoutPosition) return null;

      return createPortal(
        <div
          data-plus-menu-flyout=""
          className="pointer-events-auto fixed z-[200]"
          style={{ top: flyoutPosition.top, left: flyoutPosition.left }}
        >
          {content}
        </div>,
        document.body
      );
    }

    return (
      <>
        <div
          className="absolute left-full top-0 z-50 h-full w-1"
          onMouseEnter={() => {
            if (openSubmenu) {
              openSubmenuPanel(openSubmenu);
            }
          }}
        />
        <div
          data-plus-menu-flyout=""
          className="pointer-events-auto absolute left-full top-0 z-[150] pl-1"
          onMouseEnter={() => {
            if (openSubmenu) {
              openSubmenuPanel(openSubmenu);
            }
          }}
          onMouseLeave={() => {
            scheduleCloseSubmenu();
          }}
        >
          {content}
        </div>
      </>
    );
  };

  const renderProjectSubmenu = () => (
    <div className={projectSubmenuPanelClass}>
      {renderFlyoutSubmenuItem({
        label: t('NEW_PROJECT'),
        icon: <ConversationMenuIcon optionId="new-project" />,
        onClick: () => {
          setOpenSubmenu(null);
          runFlyoutItemAction(() => onOpenNewProject?.(), onClose);
        },
      })}
      {effectiveProjects.map((project) =>
        renderFlyoutSubmenuItem({
          label: project.name,
          icon: <ConversationMenuIcon optionId="add-to-project" />,
          onClick: () => {
            void handleAddToProject(project.id);
          },
        })
      )}
    </div>
  );

  const renderSkillsSubmenu = () => (
    <div className={projectSubmenuPanelClass}>
      {isSkillsLoading ? (
        <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('LOADING') || 'Loading...'}
        </div>
      ) : skills.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-muted-foreground">
          {t('NO_SKILLS_FOUND') || 'No skills found'}
        </p>
      ) : (
        skills.map((skill) =>
          renderFlyoutSubmenuItem({
            label: skill.title,
            icon: renderSkillSubmenuIcon(),
            onClick: () => {
              setOpenSubmenu(null);
              runFlyoutItemAction(() => onSelectSkill?.(skill), onClose);
            },
          })
        )
      )}
    </div>
  );

  const renderFooterItems = () => (
    <>
      {renderMenuButton({
        label: t('RESEARCH') || 'Research',
        icon: <Brain className="h-4 w-4" strokeWidth={1.5} />,
        disabled: true,
      })}
      {renderMenuButton({
        label: t('USE_STYLE') || 'Use style',
        icon: <ColorPicker className="h-4 w-4" strokeWidth={1.5} />,
        disabled: true,
        showChevron: true,
      })}
    </>
  );

  if (section === 'footer') {
    return (
      <>
        {showLeadingSeparator && <MenuSeparator />}
        {renderFooterItems()}
      </>
    );
  }

  return (
    <>
      {showMain && (
        <>
          <div
            className="relative"
            onMouseLeave={() => {
              if (!useTapSubmenus) {
                scheduleCloseSubmenu();
              }
            }}
          >
            <button
              ref={projectTriggerRef}
              type="button"
              disabled={!canAddToProject}
              onMouseEnter={() => {
                if (!useTapSubmenus && canAddToProject) {
                  openSubmenuPanel('project');
                }
              }}
              onClick={() => {
                if (!canAddToProject) return;
                if (useTapSubmenus) {
                  toggleSubmenu('project');
                  return;
                }
                openSubmenuPanel('project');
              }}
              className={cn(
                'flex w-full items-center gap-3 px-1 py-1.5 text-left transition-colors',
                menuItemHoverClass,
                !canAddToProject && menuItemDisabledClass,
                openSubmenu === 'project' && canAddToProject && 'bg-[#F6F6F6] dark:bg-muted'
              )}
            >
              <MenuIcon textClass={menuItemTextClass}>
                <ConversationMenuIcon optionId="add-to-project" />
              </MenuIcon>
              <div className="flex flex-1 items-center justify-between gap-2">
                <span className={menuItemLabelClass}>{t('ADD_TO_PROJECT')}</span>
                <NavArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
              </div>
            </button>

            {openSubmenu === 'project' && canAddToProject && renderFlyoutPanel(renderProjectSubmenu())}
          </div>

          <div
            className="relative"
            onMouseLeave={() => {
              if (!useTapSubmenus) {
                scheduleCloseSubmenu();
              }
            }}
          >
            <button
              ref={skillsTriggerRef}
              type="button"
              onMouseEnter={() => {
                if (!useTapSubmenus) {
                  openSubmenuPanel('skills');
                }
              }}
              onClick={() => {
                if (useTapSubmenus) {
                  toggleSubmenu('skills');
                  return;
                }
                openSubmenuPanel('skills');
              }}
              className={cn(
                'flex w-full items-center gap-3 px-1 py-1.5 text-left transition-colors',
                menuItemHoverClass,
                openSubmenu === 'skills' && 'bg-[#F6F6F6] dark:bg-muted'
              )}
            >
              <MenuIcon textClass={menuItemTextClass}>
                <DocStarIcon className="h-4 w-4" />
              </MenuIcon>
              <div className="flex flex-1 items-center justify-between gap-2">
                <span className={menuItemLabelClass}>{t('SKILLS') || 'Skills'}</span>
                <NavArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
              </div>
            </button>

            {openSubmenu === 'skills' && renderFlyoutPanel(renderSkillsSubmenu())}
          </div>

          <MenuSeparator />

          {renderMenuButton({
            label: t('INTEGRATIONS_KEY') || 'Integrations',
            icon: <Usb className="h-4 w-4" strokeWidth={1.5} />,
            onClick: () => {
              onToggleConnectors?.();
              onClose?.();
            },
            isActive: isConnectorsActive,
            showChevron: true,
          })}
        </>
      )}

      {showMain && showFooter && <MenuSeparator />}

      {showFooter && renderFooterItems()}
    </>
  );
}
